export const currentDateAsString = (): string => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0'); // Ensure two digits
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const year = today.getFullYear();

    return `${day}/${month}/${year}`;
};

export const extractAndConvertFrom = (dateTime: string) => {
    if (!dateTime) {
        return '';
    }
    return dateTime.split(' ')[0].split('-').reverse().join('/');
};

export const isDatePast = (dateString: string) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const inputDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);

    return inputDate < today;
};

export const formatDateToBR = (date: string | null | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
};

export const formatDateFromBRToISO = (dateString: string): string => {
    if (!dateString)
        return '';
    const [day, month, year] = dateString.split('/');
    if (!day || !month || !year)
        return '';
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

/**
 * Parseia uma string de data para Date local, tratando YYYY-MM-DD como data local
 * (não UTC) para evitar deslocamento de timezone.
 *
 * AF-3447: new Date("YYYY-MM-DD") é interpretado como UTC pela spec JS, o que exibe o dia
 * anterior em BRT. Timestamps ISO completos (com hora e offset) são parseados normalmente,
 * porque aí o instante é inequívoco e a conversão para o fuso local é a correta.
 *
 * AF-3581: promovido de cópias locais em MonitoringPage e ExportDropdown para cá, porque
 * componentes novos precisavam do mesmo tratamento e o formatDateToBR acima não o faz —
 * ele usa new Date() direto e sofre o off-by-one com data sem hora.
 */
export const parseLocalDate = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        const parsed = new Date(year, month - 1, day);

        // O construtor do Date normaliza valores fora do intervalo em vez de falhar:
        // new Date(2026, 1, 30) devolve 02/03/2026. O risco não é a data absurda, é a
        // plausível — 2026-02-30 apareceria na tela como 02/03 sem nenhum sinal de erro.
        // O ramo de ISO completo abaixo já é estrito (devolve Invalid Date), então sem
        // esta conferência os dois ramos teriam rigor diferente.
        const preservouOsCampos =
            parsed.getFullYear() === year &&
            parsed.getMonth() === month - 1 &&
            parsed.getDate() === day;

        return preservouOsCampos ? parsed : null;
    }

    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Data no formato brasileiro, sem deslocamento de timezone para data sem hora.
 * Prefira esta função a formatDateToBR quando a origem puder ser YYYY-MM-DD.
 */
export const formatLocalDateToBR = (dateStr: string | null | undefined): string => {
    const parsed = parseLocalDate(dateStr);
    return parsed ? parsed.toLocaleDateString('pt-BR') : '';
};
